import { maxNumberQuotesInCart } from '../../../../agencies/quotes/PrimeX/constants';
import isEqual from 'lodash/isEqual';
import type { CoverageOption } from '../../../../../api/submissions/submission-coverages.types';

// One cap for the cart and for saved options: the cart modal and its badge share it.
export const MAX_OPTIONS_PER_PRODUCT: number = maxNumberQuotesInCart;

export const hasQuote = (option: CoverageOption): boolean =>
  Boolean(option.quoteNumber) || (option.quotes?.length ?? 0) > 0;

export const unquotedOptionsFor = (
  product: string,
  options: CoverageOption[]
): CoverageOption[] =>
  options.filter((option) => option.product === product && !hasQuote(option));

export const canAddOption = (
  product: string,
  options: CoverageOption[],
  max = MAX_OPTIONS_PER_PRODUCT
): boolean => unquotedOptionsFor(product, options).length < max;

/** Deep equality that ignores object key order. Array order still matters. */
export const isDuplicateTerms = (a: unknown, b: unknown): boolean =>
  isEqual(a, b);

/**
 * Mirrors the backend CoverageOptionCompleteness rule: partial means no product,
 * or no part with a limit and no tower.
 */
export const isPartial = (option: CoverageOption): boolean => {
  if (!option.product) {
    return true;
  }
  const hasLimit = option.coverages.some(
    (coverage) => coverage.limit?.value != null
  );
  return !hasLimit && !option.tower;
};

/**
 * The terms exactly as the wire carries them. A saved option's terms come back from JSON, so a
 * payload with an undefined field or a NaN must be compared in that same form.
 */
export const toOptionTerms = (payload: unknown): unknown =>
  JSON.parse(JSON.stringify(payload));

/** True when an unquoted option of this product already holds these terms. */
export const hasOptionWithTerms = (
  product: string,
  options: CoverageOption[],
  terms: unknown
): boolean => {
  const wanted = toOptionTerms(terms);
  return unquotedOptionsFor(product, options).some((option) =>
    isDuplicateTerms(option.terms, wanted)
  );
};
